import { TraversalBuilderViewState, TripleOptions } from '../types';
import { StartSetExpansion } from '@ardoq/api-types';
import { DoqContainer, SidebarHeader } from './atoms';
import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { EditTraversalCommands } from 'viewpointBuilder/traversals/editTraversalCommands';
import { SidebarMenu } from '@ardoq/sidebar-menu';
import { DeleteIcon } from '@ardoq/icons';
import { TriplesToggler } from './TriplesToggler';
import { IncludeDescendantsCard } from './IncludeDescendantsCard';
import { Box, FlexBox } from '@ardoq/layout';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { GhostButton } from '@ardoq/button';
import { WildcardTraversalPanel } from './WildcardTraversalPanel';
import { t, type I18nLocale } from '@ardoq/i18n';

type EditTraversalOptionsProps = {
  selectedGraphNodeId: string | null;
  tripleOptions: TripleOptions;
  commands: EditTraversalCommands;
  triplesSortAndFiltersState: TraversalBuilderViewState['triplesSortAndFiltersState'];
  hasTripleOptions: TraversalBuilderViewState['hasTripleOptions'];
  shouldShowDeleteCurrentNodeButton: boolean;
  shouldShowStartSetExpansionToggle: boolean;
  hierarchyComponentTypes: TraversalBuilderViewState['hierarchyComponentTypes'];
  startSetExpansion: StartSetExpansion | null;
  isFullWidth?: boolean;
  wildcardState: TraversalBuilderViewState['wildcardState'];
  i18nLocale: I18nLocale;
};

export const EditTraversalOptionsPanel = ({
  selectedGraphNodeId,
  tripleOptions,
  commands,
  triplesSortAndFiltersState,
  hasTripleOptions,
  shouldShowDeleteCurrentNodeButton,
  shouldShowStartSetExpansionToggle,
  hierarchyComponentTypes,
  startSetExpansion,
  isFullWidth = false,
  wildcardState,
  i18nLocale,
}: EditTraversalOptionsProps) => {
  if (wildcardState) {
    return (
      <WildcardTraversalPanel commands={commands} i18nLocale={i18nLocale} />
    );
  }

  if (!selectedGraphNodeId) {
    return (
      <FlexBox
        height="full"
        width="full"
        gap="medium"
        backgroundColor="surfaceDefault"
        justify="center"
      >
        <DoqContainer>
          <ResponsiveDoqWithSpeechBubble
            doqType={DoqType.SIMPLE}
            title={t(
              i18nLocale,
              'viewpointBuilder:editTraversal.noSelection.title',
              'Start with a component type'
            )}
            message={t(
              i18nLocale,
              'viewpointBuilder:editTraversal.noSelection.message',
              'Select a component type to see its available references'
            )}
          />
        </DoqContainer>
      </FlexBox>
    );
  }

  return (
    <SidebarMenu
      isPageBodyMenu
      isResizable
      isRightContent
      isFullWidth={isFullWidth}
      headerBody={
        <SidebarHeader
          type="component"
          selectedType={tripleOptions.selectedNode.metaDataComponent}
          headerText={t(
            i18nLocale,
            'viewpointBuilder:editTraversal.header',
            'Select references for:'
          )}
          rightContent={
            shouldShowDeleteCurrentNodeButton ? (
              <DeleteCurrentNodeButton
                onClick={() =>
                  commands.removeNodeFromGraph(selectedGraphNodeId)
                }
                i18nLocale={i18nLocale}
              />
            ) : null
          }
        />
      }
    >
      {shouldShowStartSetExpansionToggle && (
        <Box paddingX="medium" paddingTop="medium" paddingBottom="small">
          <IncludeDescendantsCard
            isChecked={startSetExpansion === 'descendants'}
            // The option counts are refetched on toggle, and toggling again
            // while they are in flight would race them.
            isDisabled={tripleOptions.optionCountLoadingState === 'loading'}
            onToggle={isChecked =>
              commands.setStartSetExpansion(isChecked ? 'descendants' : null)
            }
            hierarchyComponentTypes={hierarchyComponentTypes}
            i18nLocale={i18nLocale}
          />
        </Box>
      )}
      <Box padding="small">
        <TriplesToggler
          isDisabledWholeRow={true}
          tripleOptions={tripleOptions}
          commands={commands}
          triplesSortAndFiltersState={triplesSortAndFiltersState}
          hasTripleOptions={hasTripleOptions}
          i18nLocale={i18nLocale}
        />
      </Box>
    </SidebarMenu>
  );
};

type DeleteCurrentNodeButtonProps = {
  onClick: () => void;
  i18nLocale: I18nLocale;
};

const DeleteCurrentNodeButton = ({
  onClick,
  i18nLocale,
}: DeleteCurrentNodeButtonProps) => {
  return (
    <GhostButton onClick={onClick}>
      <DeleteIcon />
      <VisuallyHidden>
        {t(
          i18nLocale,
          'viewpointBuilder:editTraversal.removeFromGraph',
          'Remove from graph'
        )}
      </VisuallyHidden>
    </GhostButton>
  );
};
